import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { parseGcpServiceAccount } from '@couch/shared';
import { useState, type SyntheticEvent } from 'react';
import { api, errorMessage } from '../../api/client';
import { cloudApi } from '../../api/cloud';
import { CopyField } from '../../components/CopyField';
import { CLOUD_KEYS_KEY } from './RunPodKeyPanel';

/** Opt-in: the key file on Couch, after the user confirmed what it can do (doc 67). */
function KeepOnCouch({ onDone }: { onDone: () => void }) {
  const queryClient = useQueryClient();
  const [text, setText] = useState('');
  const [understood, setUnderstood] = useState(false);
  const parsed = text.trim() ? parseGcpServiceAccount(text) : null;
  const save = useMutation({
    mutationFn: () => cloudApi.setGcpKey(text.trim()),
    onSuccess: async () => { setText(''); onDone(); await queryClient.invalidateQueries({ queryKey: CLOUD_KEYS_KEY }); },
  });
  const submit = (e: SyntheticEvent) => { e.preventDefault(); save.mutate(); };
  return (
    <form className="runpod-key-form" onSubmit={submit}>
      <label htmlFor="gcp-key">The key file (JSON) — open it in a text editor and paste all of it</label>
      <textarea id="gcp-key" rows={6} autoComplete="off" spellCheck={false} value={text} onChange={(e) => { setText(e.target.value); }} />
      {parsed && 'problem' in parsed && <p role="alert" className="form-error">{parsed.problem}</p>}
      {parsed && !('problem' in parsed) && <p className="muted">Project {parsed.projectId}, service account {parsed.clientEmail}.</p>}
      <label className="checkbox">
        <input type="checkbox" checked={understood} onChange={(e) => { setUnderstood(e.target.checked); }} />
        I understand: kept encrypted on Couch, this key could create and delete VMs in this project if Couch were broken into.
      </label>
      {save.isError && <p role="alert" className="form-error">{errorMessage(save.error)}</p>}
      <button type="submit" disabled={!understood || !parsed || 'problem' in parsed || save.isPending}>Save on Couch</button>
    </form>
  );
}

/** Where the user's Google Cloud key is — never the key itself (doc 67). */
export function GcpKeyPanel() {
  const queryClient = useQueryClient();
  const [opting, setOpting] = useState(false);
  const keys = useQuery({ queryKey: CLOUD_KEYS_KEY, queryFn: api.cloudKeys });
  const remove = useMutation({ mutationFn: cloudApi.removeGcpKey, onSuccess: () => queryClient.invalidateQueries({ queryKey: CLOUD_KEYS_KEY }) });
  const holders = keys.data?.gcp;
  return (
    <div className="runpod-key">
      <h3>Your Google Cloud key</h3>
      {keys.isError && <p role="alert" className="form-error">{errorMessage(keys.error)}</p>}
      {holders?.machines.map((m) => <p key={m.id} className="success">{m.name} holds a key{m.online ? '' : ' (offline)'}.</p>)}
      <h4>On a home machine (recommended — Couch never sees the key)</h4>
      <p>In a terminal on that machine, with the path of the downloaded file. Afterwards you can delete the file:</p>
      <CopyField value="~/Couch/venv/bin/couch-agent cloud-key set gcp --file ~/Downloads/KEY-FILE.json" label="Keep the key on this Mac or Linux machine" />
      <CopyField value={'%USERPROFILE%\\Couch\\venv\\Scripts\\couch-agent.exe cloud-key set gcp --file %USERPROFILE%\\Downloads\\KEY-FILE.json'} label="Keep the key on this Windows PC" />
      <p className="muted">Needs agent 0.13.0 or newer (Update agent on the machine card).</p>
      <h4>On Couch (opt-in)</h4>
      {holders?.server.set ? (
        <p>
          A key for project <strong>{holders.projectId}</strong> is kept on Couch, encrypted.{' '}
          <button type="button" className="button-quiet" disabled={remove.isPending} onClick={() => { remove.mutate(); }}>Remove the key from Couch</button>
        </p>
      ) : opting ? (
        <KeepOnCouch onDone={() => { setOpting(false); }} />
      ) : (
        <p>
          Then Couch can also start a VM for a waiting run when no home machine is on.{' '}
          <button type="button" className="button-quiet" onClick={() => { setOpting(true); }}>Keep my key on Couch…</button>
        </p>
      )}
    </div>
  );
}
